import axios from "axios";

const isServer = typeof window === "undefined";

const api = axios.create({
  baseURL: isServer ? "http://127.0.0.1:5000/api" : import.meta.env.VITE_API_URL || "/api",
});

// Add request interceptor for debugging in SSR
if (isServer) {
  api.interceptors.request.use(
    (config) => {
      console.log(`[SSR API] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
      return config;
    },
    (error) => {
      console.error("[SSR API] Request Error:", error);
      return Promise.reject(error);
    },
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      console.error(
        `[SSR API] Response Error [${error.config?.method?.toUpperCase()} ${error.config?.url}]:`,
        error.message,
      );
      return Promise.reject(error);
    },
  );
}

export default api;

export const fetchArticles = async () => {
  const { data } = await api.get("/articles");
  return data;
};

export const fetchArticleBySlug = async (slug: string) => {
  const { data } = await api.get(`/articles/${slug}`);
  return data;
};

export const fetchNovels = async () => {
  const { data } = await api.get("/novels");
  return data;
};

export const fetchNovelBySlug = async (slug: string) => {
  const { data } = await api.get(`/novels/${slug}`);
  return data;
};

export const fetchCategories = async () => {
  const { data } = await api.get("/categories");
  return data;
};

export const fetchTags = async () => {
  const { data } = await api.get("/tags");
  return data;
};

export const fetchAuthors = async () => {
  const { data } = await api.get("/authors");
  return data;
};

export const fetchAuthorBySlug = async (slug: string) => {
  const { data } = await api.get(`/authors/${slug}`);
  return data;
};

export const fetchCategoryPosts = async (slug: string) => {
  const { data } = await api.get(`/categories/${slug}/posts`);
  return data;
};

export const fetchTagPosts = async (slug: string) => {
  const { data } = await api.get(`/tags/${slug}/posts`);
  return data;
};

export const fetchComments = async (postId: number, chapterId?: number) => {
  const url = chapterId ? `/comments/${postId}?chapterId=${chapterId}` : `/comments/${postId}`;
  const { data } = await api.get(url);
  return data;
};

export const postComment = async (payload: {
  content: string;
  postId: number;
  chapterId?: number;
  authorName?: string;
  authorEmail?: string;
  parentId?: number;
  website?: string;
}) => {
  const token = !isServer ? localStorage.getItem("aksara_token") : null;
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  const { data } = await api.post("/comments", payload, config);
  return data;
};

export const searchContent = async (q: string) => {
  const { data } = await api.get(`/search?q=${q}`);
  return data;
};

export const fetchFeaturedPosts = async () => {
  const { data } = await api.get("/featured-posts");
  return data;
};

export const fetchSettings = async () => {
  const { data } = await api.get("/settings");
  return data;
};

export const subscribeNewsletter = async (email: string) => {
  const { data } = await api.post("/subscribe", { email });
  return data;
};

export const fetchChapter = async (novelSlug: string, chapterSlug: string) => {
  const { data } = await api.get(`/novels/${novelSlug}/chapters/${chapterSlug}`);
  return data;
};
